/* DevUI mobile collection drawer: menu-driven collection list + intelligent image fallback. */
(() => {
  const ROOT_ID = 'DevuiMobileBottomNav';
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let cacheSignature = '';
  let loadingPromise = null;
  let searchBound = false;

  const shopRoot = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const collectionHandleFromHref = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/collections\/([^/]+)/i);
      if (!match) return '';
      return decodeURIComponent(match[1]).trim();
    } catch (_) {
      return '';
    }
  };

  const menuCollections = () => {
    const links = document.querySelectorAll(
      'header-drawer .menu-drawer__navigation a[href*="/collections/"]'
    );
    const seen = new Set();
    const items = [];

    links.forEach((link) => {
      const handle = collectionHandleFromHref(link.href);
      if (!handle || seen.has(handle)) return;
      seen.add(handle);

      const label = link.querySelector('.menu-drawer__menu-item-text')?.textContent || link.textContent || '';
      const title = label.replace(/\s+/g, ' ').trim();
      if (!title) return;

      const image = link.querySelector('img')?.currentSrc || link.querySelector('img')?.src || '';
      items.push({ handle, title, href: link.href, menuImage: image });
    });

    return items;
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  };

  const fetchCollectionMeta = async (wantedHandles) => {
    const wanted = new Set(wantedHandles.filter((handle) => handle && handle !== 'all'));
    const found = new Map();
    if (!wanted.size) return found;

    for (let page = 1; page <= 12 && found.size < wanted.size; page += 1) {
      const json = await fetchJson(`${shopRoot()}collections.json?limit=250&page=${page}`);
      const batch = Array.isArray(json.collections) ? json.collections : [];

      batch.forEach((collection) => {
        if (!collection?.handle || !wanted.has(collection.handle)) return;
        found.set(collection.handle, collection);
      });

      if (batch.length < 250) break;
    }

    return found;
  };

  const firstProductImage = async (handle) => {
    try {
      const json = await fetchJson(
        `${shopRoot()}collections/${encodeURIComponent(handle)}/products.json?limit=1`
      );
      const product = Array.isArray(json.products) ? json.products[0] : null;
      const firstImage = product?.images?.[0];
      if (typeof firstImage === 'string') return firstImage;
      return firstImage?.src || product?.image?.src || '';
    } catch (_) {
      return '';
    }
  };

  const resolveImages = async (items) => {
    const metadata = await fetchCollectionMeta(items.map((item) => item.handle)).catch(() => new Map());

    return Promise.all(
      items.map(async (item) => {
        const collection = metadata.get(item.handle);
        const collectionImage = collection?.image?.src || collection?.image?.url || '';
        const productImage = collectionImage ? '' : await firstProductImage(item.handle);
        const image = collectionImage || productImage || item.menuImage;
        return { ...item, image };
      })
    );
  };

  const skeleton = () =>
    Array.from(
      { length: 6 },
      () => '<div aria-hidden="true"><div class="sk-media"></div><div class="sk-line"></div></div>'
    ).join('');

  const createCard = (item) => {
    const card = document.createElement('a');
    card.className = 'cc';
    card.href = item.href || `${shopRoot()}collections/${encodeURIComponent(item.handle)}`;
    card.dataset.card = '';
    card.dataset.title = item.title.toLocaleLowerCase();
    card.setAttribute('role', 'listitem');

    const media = document.createElement('span');
    media.className = 'cc-media';

    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'cc-fallback';
      fallback.textContent = item.title.slice(0, 1);
      media.appendChild(fallback);
    }

    const name = document.createElement('span');
    name.className = 'cc-name';
    name.textContent = item.title;

    card.append(media, name);
    return card;
  };

  const applyFilter = (root) => {
    const input = root.querySelector('[data-search]');
    const grid = root.querySelector('[data-grid]');
    const empty = root.querySelector('[data-empty]');
    if (!input || !grid || !empty) return;

    const query = input.value.trim().toLocaleLowerCase();
    let visible = 0;

    grid.querySelectorAll('[data-card]').forEach((card) => {
      const matches = !query || (card.dataset.title || '').includes(query);
      card.hidden = !matches;
      if (matches) visible += 1;
    });

    empty.hidden = visible > 0 || !query;
  };

  const render = (root, items) => {
    const grid = root.querySelector('[data-grid]');
    const meta = root.querySelector('[data-meta]');
    const empty = root.querySelector('[data-empty]');
    if (!grid) return;

    grid.replaceChildren();
    grid.setAttribute('role', 'list');
    items.forEach((item) => grid.appendChild(createCard(item)));

    if (meta) {
      meta.textContent = `${items.length} menu collection${items.length === 1 ? '' : 's'} ready to explore.`;
    }

    if (!items.length) {
      const message = document.createElement('div');
      message.className = 'dc-empty';
      message.innerHTML = '<div><strong>No collection links found in your menu</strong><span>Add collection links to the store menu and they will appear here automatically.</span></div>';
      grid.appendChild(message);
      if (meta) meta.textContent = 'Collections shown here follow your menu.';
    }

    if (empty) empty.hidden = true;
    applyFilter(root);
  };

  const renderError = (root) => {
    const grid = root.querySelector('[data-grid]');
    const meta = root.querySelector('[data-meta]');
    if (!grid) return;

    const menuItems = menuCollections();
    grid.replaceChildren();
    grid.setAttribute('role', 'list');
    menuItems.forEach((item) => grid.appendChild(createCard(item)));
    if (meta) meta.textContent = 'Collections shown here follow your menu.';
  };

  const loadMenuCollections = (root, force = false) => {
    const menuItems = menuCollections();
    const signature = menuItems.map((item) => `${item.handle}:${item.title}`).join('|');

    if (!force && signature && signature === cacheSignature && root.querySelector('[data-grid] [data-card]')) {
      return Promise.resolve();
    }
    if (loadingPromise) return loadingPromise;

    const grid = root.querySelector('[data-grid]');
    const meta = root.querySelector('[data-meta]');
    if (grid) grid.innerHTML = skeleton();
    if (meta) meta.textContent = 'Loading your menu collections…';

    loadingPromise = resolveImages(menuItems)
      .then((items) => {
        cacheSignature = signature;
        render(root, items);
      })
      .catch(() => renderError(root))
      .finally(() => {
        loadingPromise = null;
      });

    return loadingPromise;
  };

  const markCollectionActive = (trigger) => {
    if (!trigger) return;
    if (!Object.prototype.hasOwnProperty.call(trigger.dataset, 'was')) {
      trigger.dataset.was = trigger.dataset.active || 'false';
    }
    trigger.dataset.active = 'true';
  };

  const openDrawer = (root) => {
    if (!mobileQuery.matches) return;
    const layer = root.querySelector('[data-layer]');
    const trigger = root.querySelector('[data-action="collections"]');
    if (!layer) return;

    layer.classList.add('open');
    layer.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('devui-bnav-lock');
    markCollectionActive(trigger);

    window.setTimeout(() => {
      layer.querySelector('.dc-close')?.focus({ preventScroll: true });
    }, 80);

    loadMenuCollections(root);
  };

  const bind = (root) => {
    if (root.dataset.menuCollectionsReady === 'true') return;
    root.dataset.menuCollectionsReady = 'true';

    root.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-action="collections"]');
        if (!trigger || !root.contains(trigger)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openDrawer(root);
      },
      true
    );

    const input = root.querySelector('[data-search]');
    if (input && !searchBound) {
      searchBound = true;
      input.addEventListener('input', () => applyFilter(root));
    }
  };

  const boot = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) {
      window.setTimeout(boot, 80);
      return;
    }
    bind(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    cacheSignature = '';
    window.setTimeout(boot, 0);
  });
})();
